import { useEffect, type Dispatch } from "react";
import { useRouter } from "@/routing/navigation";
import { ROUTES } from "@/routes/routes";
import {
  storagesApi,
  companyProfilesApi,
  usersApi,
} from "@/lib/api";
import { fetchAllPopulations } from "@/lib/homepage-personalization";
import { toast } from "sonner";
import i18n from "@/i18n";
import { readUntilAnswered } from "@/features/studies/questionnaire/readUntilAnswered";
import { pageReadFinal } from "@/lib/api/transportFailure";
import type { CreationState, CreationAction } from "../state/creationState";

export function useStudyCreationData(
  state: CreationState,
  dispatch: Dispatch<CreationAction>,
): void {
  const router = useRouter();

  useEffect(() => {
    let isMounted = true;

    const initialize = async () => {
      const user = localStorage.getItem("user");
      if (!user) {
        router.push(ROUTES.login);
        return;
      }

      try {
        const rawUser = localStorage.getItem("user");
        if (rawUser) {
          const user = JSON.parse(rawUser);
          if (user?.featureFlags?.createStudy === false) {
            router.push(ROUTES.home);
            return;
          }
          if (user?.creditInfo && isMounted) {
            dispatch({ type: "SET_CREDIT_INFO", payload: user.creditInfo });
          }
        }
      } catch (error) {
        console.error("Failed to parse user from localStorage:", error);
      }

      if (isMounted) {
        dispatch({ type: "SET_AUTH_READY", payload: true });
      }

      try {
        const latestCreditInfo = await usersApi.getCreditInfo();
        if (isMounted) {
          dispatch({ type: "SET_CREDIT_INFO", payload: latestCreditInfo });
        }
      } catch (error) {
        console.error("Failed to refresh credit info:", error);
      }
    };

    initialize();
    return () => {
      isMounted = false;
    };
  }, [router, dispatch]);

  useEffect(() => {
    if (!state.authReady || state.audienceDataLoaded)
      return;

    // The page's own reads: a read with no answer (offline, the backend
    // restarting, a 5xx) is sent again until it answers, so a blip never ends
    // the flow on the load error (MT-06); any other failure does. Unmounting
    // stops them.
    const unmounted = new AbortController();

    const loadAudienceData = async () => {
      dispatch({ type: "SET_AUDIENCE_LOADING", payload: true });
      try {
        const [populationsData, storagesData, profilesData] = await Promise.all(
          [
            readUntilAnswered(fetchAllPopulations, unmounted.signal, pageReadFinal),
            readUntilAnswered(() => storagesApi.list(), unmounted.signal, pageReadFinal),
            readUntilAnswered(() => companyProfilesApi.list(), unmounted.signal, pageReadFinal),
          ],
        );
        if (unmounted.signal.aborted) return;
        dispatch({
          type: "SET_AUDIENCE_DATA",
          payload: {
            populations: populationsData,
            storages: storagesData,
            companyProfiles: profilesData,
          },
        });
      } catch (error) {
        if (unmounted.signal.aborted) return;
        console.error("Failed to load audiences/knowledge:", error);
        toast.error(i18n.t("creation.failedLoadAudiencesContext", { ns: "app" }));
        dispatch({ type: "SET_AUDIENCE_LOAD_ERROR", payload: true });
      } finally {
        if (!unmounted.signal.aborted) {
          dispatch({ type: "SET_AUDIENCE_LOADING", payload: false });
        }
      }
    };

    loadAudienceData();
    return () => {
      unmounted.abort();
    };
  }, [state.authReady, state.audienceDataLoaded, dispatch]);

  useEffect(() => {
    if (state.currentStep !== 3) return;
    let isMounted = true;
    usersApi
      .getCreditInfo()
      .then((info) => {
        if (isMounted) dispatch({ type: "SET_CREDIT_INFO", payload: info });
      })
      .catch((err) => console.error("Failed to refresh credit info:", err));
    return () => {
      isMounted = false;
    };
  }, [state.currentStep, dispatch]);
}
